import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import moment from 'moment';
import scaleSize from 'utils/scale-size';
import { getImageUrlWithResizer } from 'utils/image';
import { LIST_ITEMS_SIZE } from 'config';
import { Focusable } from 'components/navigation';

export const LIST_ITEM_MARGIN = scaleSize(19);

type ItemSizeType = { width: number; height: number; metadataHeight: number };

interface IListProgramItemWrapper {
  itemSize: ItemSizeType;
}

const ListProgramItemWrapper = styled(Focusable)<IListProgramItemWrapper>`
  position: relative;
  width: ${({ itemSize }) => scaleSize(itemSize.width)}px;
  height: ${({ itemSize }) => scaleSize(itemSize.height + itemSize.metadataHeight)}px;
  display: inline-block;
  text-align: center;
  margin-right: ${scaleSize(LIST_ITEM_MARGIN)}px;
  box-sizing: border-box;
  cursor: pointer;
  vertical-align: top;
  position: relative;
  background: #26262c;
  border-radius: ${scaleSize(12)}px;
  border: ${scaleSize(8)}px solid transparent;

  &:focus {
    border: ${scaleSize(8)}px solid ${({ theme }) => theme.activeColor};
    transform: translate3d(0, -${scaleSize(20)}px, 0);
  }
`;

const ProgramImageWrapper = styled.div`
  height: ${scaleSize(409)}px;
  background-size: ${scaleSize(730)}px ${scaleSize(409)}px;
  position: relative;
`;

const Logo = styled.div.attrs<{ url: string }>((props) => {
  return {
    style: {
      backgroundImage: `url(${props.url})`,
    },
  };
})<{ url: string }>`
  width: ${scaleSize(100)}px;
  height: ${scaleSize(100)}px;
  position: absolute;
  bottom: ${scaleSize(100)}px;
  left: ${scaleSize(60)}px;
  background-size: ${scaleSize(100)}px;
  background-color: #191919;
`;

const StyledProgramTile = styled.div`
  font-family: RobotoCondensedBold;
  font-style: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${scaleSize(45)}px;
  color: #f4f4f4;
  position: absolute;
  top: ${scaleSize(99)}px;
  left: ${scaleSize(75)}px;
  max-width: ${scaleSize(600)}px;
`;

const StyledProgramMeta = styled.div`
  font-size: ${scaleSize(36)}px;
  color: #ccc;
  position: absolute;
  font-weight: 400;
  left: ${scaleSize(195)}px;
  bottom: ${scaleSize(100)}px;
  min-height: 70px;
`;

const StyledProgramTime = styled.div`
  font-family: RobotoRegular;
  vertical-align: top;
  color: #cccccc;
  position: relative;
  overflow: visible;
  font-size: ${scaleSize(24)}px;
`;

const LiveLabel = styled.div`
  color: #f33;
  position: relative;
  padding-left: ${scaleSize(24)}px;
  font-size: ${scaleSize(24)}px;
  text-transform: uppercase;
  text-align: left;
  line-height: 1.35;

  &:after {
    content: '';
    background-color: #f33;
    position: absolute;
    top: 50%;
    width: ${scaleSize(12)}px;
    height: ${scaleSize(12)}px;
    margin-top: -${scaleSize(6)}px;
    left: 0;
    border-radius: ${scaleSize(12)}px;
  }
`;

const ProgramListItem = (props) => {
  const { id, index, item } = props;
  const { onFocus, onEnter, className } = props;
  const itemSize = LIST_ITEMS_SIZE.PROGRAM;
  const isLive = item.isLive;
  const imgUrl = getImageUrlWithResizer(item.currentChannel, { width: 100, height: 100 });

  const day = moment(item.start_time).locale('en').format('MMMM Do');

  const time = useMemo(() => {
    const s = moment(item.start_time).format('HH:mm');
    const e = moment(item.end_time).format('HH:mm');
    return `${s} - ${e}`;
  }, [item]);

  return (
    <ListProgramItemWrapper
      key={id}
      id={id}
      className={className}
      onFocus={onFocus}
      onEnter={onEnter}
      payload={index}
      index={index}
      itemSize={itemSize}
    >
      <ProgramImageWrapper>
        <Logo url={imgUrl} />
      </ProgramImageWrapper>
      <StyledProgramTile>{item.name}</StyledProgramTile>
      <StyledProgramMeta>
        <StyledProgramTime>
          {day}, {time}
        </StyledProgramTime>
        {isLive && <LiveLabel>Live</LiveLabel>}
      </StyledProgramMeta>
    </ListProgramItemWrapper>
  );
};

export default ProgramListItem;
